import React, { useState, useEffect, useRef, useCallback } from 'react';
import { string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { Icon } from '@atoms';
import { Flex, Typography } from '@mixins';
import { storage } from '@utils';
import { trackEvent } from '@utils/mixpanel';
import { HEADER_ANNOUNCEMENT_KEY } from '@constants/common';
import { useOutsideClick, useDebounce } from '@hooks';
import { ReactComponent as Location } from '@assets/svgs/Location.svg';
import { getLocations, getCurrentLocation } from '@store/location/duck';

import LocationList from '../LocationList';

const LocationChanger = ({ currentLocation }) => {
  const locationsRef = useRef();
  const buttonRef = useRef();
  const textRef = useRef();

  const headerAnnouncement = storage.getFromLocalStorage(HEADER_ANNOUNCEMENT_KEY);
  const parsedHeaderAnnouncement = JSON.parse(headerAnnouncement);

  const { currentLocation: location } = useSelector(store => store.locationStore);

  const { isOpen, setIsOpen } = useOutsideClick(false, [locationsRef, buttonRef, textRef]);
  const [searchInput, setSearchInput] = useState('');

  const debouncedInput = useDebounce(searchInput, 300);
  const dispatch = useDispatch();

  const handleStatusOpenChange = useCallback(() => {
    if (!location.isMindspace) return;
    setSearchInput('');
    setIsOpen(prevValue => !prevValue);
  }, [location]);

  const handleLocationSelect = id => () => {
    handleStatusOpenChange();

    trackEvent('Location changed', {
      scope: 'Locations',
      locationId: id,
    });

    if (location.id !== id) {
      dispatch(getCurrentLocation(id));
      storage.setToLocalStorage(
        HEADER_ANNOUNCEMENT_KEY,
        JSON.stringify({
          id: parsedHeaderAnnouncement?.id,
          locationId: null,
        }),
      );
    }
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getLocations({ search: debouncedInput }));
    }
  }, [debouncedInput, isOpen]);

  return (
    <>
      <Flex alignItems="center">
        <Icon
          ref={buttonRef}
          onClick={handleStatusOpenChange}
          pointer={location.isMindspace}
          ml={['8px', 15, 24]}
          SVG={Location}
          width={[6, 10]}
          height={[8, 12]}
        />
        <Typography
          ref={textRef}
          onClick={handleStatusOpenChange}
          pointer={location.isMindspace}
          pl={['5px', '7px']}
          variant="diatype-400"
          fontSize={['8px', '12px', '16px']}>
          {currentLocation.includes('Mindspace ') ? currentLocation.split('Mindspace ')[1] : currentLocation}
        </Typography>
      </Flex>
      {isOpen && (
        <LocationList
          selectedLocationId={location.id}
          topPosition={[35, 47, 60]}
          leftPosition={[92, 196, 260]}
          onLocationSelect={handleLocationSelect}
          isOpen={isOpen}
          ref={locationsRef}
          onOpenStatusChange={handleStatusOpenChange}
        />
      )}
    </>
  );
};

LocationChanger.defaultProps = {
  currentLocation: '',
};

LocationChanger.propTypes = {
  currentLocation: string,
};

export default LocationChanger;
